import React, { useState } from 'react';
import { FaBookOpen, FaCalculator } from 'react-icons/fa';

const ConfirmModal = ({ isOpen, message, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm z-50">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-6 w-80 mx-4">
        <p className="text-slate-700 text-base mb-5">{message}</p>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 rounded-lg text-sm text-white bg-blue-600 hover:bg-blue-500 transition-colors">Confirm</button>
        </div>
      </div>
    </div>
  );
};

const getInitials = (str) => {
  const skip = new Set(['and', 'of', 'the', 'in', 'a', 'an']);
  const words = str.split(/\s+/).filter(w => !skip.has(w.toLowerCase()));
  return words.slice(0, 2).map(w => w[0].toUpperCase()).join('');
};

const getEnglishGradient = (category) => {
  const map = {
    'Information and Ideas':         ['#1e3a8a', '#2563eb'],
    'Craft and Structure':           ['#312e81', '#4f46e5'],
    'Expression of Ideas':           ['#5b21b6', '#7c3aed'],
    'Standard English Conventions':  ['#0f4c81', '#0ea5e9'],
  };
  return map[category] || ['#1d4ed8', '#3b82f6'];
};

const getMathGradient = (category) => {
  const idx = parseInt(category.replace(/\D/g, '')) || 1;
  const list = [
    ['#064e3b', '#059669'],
    ['#065f46', '#10b981'],
    ['#14532d', '#22c55e'],
    ['#166534', '#4ade80'],
    ['#134e4a', '#0d9488'],
    ['#0f4c81', '#0284c7'],
    ['#1e1b4b', '#6366f1'],
    ['#4a1d96', '#8b5cf6'],
  ];
  return list[(idx - 1) % list.length];
};

const SatCourses = ({ esubjects, msubjects, handleSubTopicClick, hideCourseTabs = false }) => {
  const [activeTab, setActiveTab] = useState('english');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");

  if (hideCourseTabs) return null;

  const handleCourseSelect = (course) => handleSubTopicClick(course.cid);
  const handleCreateAICourse = () => { setConfirmMessage("Coming Soon!"); setShowConfirmModal(true); };

  const buildButtons = (subjects, isEnglish) =>
    subjects.flatMap(subject =>
      subject.subTopics.map(subtopic => ({
        cid: subject.id,
        id: `${subject.name}_${subtopic.name}`.replace(/\s+/g, '_').toLowerCase(),
        label: subtopic.name,
        parentCategory: subject.name,
        description: `Master ${subtopic.name} — ${subject.name}`,
        gradient: isEnglish ? getEnglishGradient(subject.name) : getMathGradient(subject.name),
        initials: getInitials(subject.name),
        isEnglish,
      }))
    );

  const englishCourseButtons = buildButtons(esubjects, true);
  const mathCourseButtons = buildButtons(msubjects, false);
  const cards = activeTab === 'english' ? englishCourseButtons : mathCourseButtons;

  return (
    <div>
      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setActiveTab('english')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
            activeTab === 'english'
              ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          <FaBookOpen size={12} /> English
        </button>
        <button
          onClick={() => setActiveTab('math')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
            activeTab === 'math'
              ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
              : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          <FaCalculator size={12} /> Math
        </button>
        <button
          onClick={() => handleSubTopicClick(null)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100 transition-colors"
        >
          <FaBookOpen size={12} /> SAT Mathbook Tutorial
        </button>
      </div>

      {/* Course cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {cards.map((course) => {
          const [c1, c2] = course.gradient;
          return (
            <div
              key={course.id}
              onClick={() => handleCourseSelect(course)}
              className="group bg-white border border-slate-200 rounded-xl overflow-hidden cursor-pointer shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
            >
              {/* Colored header */}
              <div
                className="relative h-28 flex items-center justify-center overflow-hidden"
                style={{ background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)` }}
              >
                {/* Decorative circles */}
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-white/10" />
                <div className="absolute -bottom-8 -left-4 w-28 h-28 rounded-full bg-white/10" />
                <div className="absolute top-3 left-3 w-4 h-4 rounded-full bg-white/20" />
                {/* Initials */}
                <span className="relative z-10 text-5xl font-black text-white/80 tracking-tighter select-none">
                  {course.initials}
                </span>
              </div>

              {/* Card body */}
              <div className="px-4 py-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] mb-1" style={{ color: c2 }}>
                  {course.isEnglish ? 'Reading & Writing' : 'Mathematics'}
                </p>
                <h3 className="text-sm font-bold text-slate-800 leading-snug mb-1.5">
                  {course.parentCategory}
                </h3>
                <p className="text-xs text-slate-400 mb-4 leading-relaxed line-clamp-2">
                  {course.label}
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <span className="text-xs text-slate-400 truncate mr-2">Click to start</span>
                  <span
                    className="text-xs font-bold px-2.5 py-1 rounded-full shrink-0 transition-colors"
                    style={{
                      background: `${c2}15`,
                      color: c2,
                      border: `1px solid ${c2}30`,
                    }}
                  >
                    Open →
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmModal
        isOpen={showConfirmModal}
        message={confirmMessage}
        onConfirm={() => setShowConfirmModal(false)}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
};

export default SatCourses;
